import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { AdyenPaymentDayCount } from '../../types/adyenPaymentDays';

export default function AdyenPaymentDaysDialog({
  accountId,
  accountName,
  onLoad,
  onClose,
}: {
  accountId: number;
  accountName: string;
  onLoad: (accountId: number) => Promise<AdyenPaymentDayCount[]>;
  onClose: () => void;
}) {
  // Local flag rather than useApi's: the spinner has to show from the first paint, before the
  // fetch effect runs, and a failed load must render differently from a genuinely empty account.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState<AdyenPaymentDayCount[] | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    onLoad(accountId).then(
      (result) => {
        if (!active) return;
        setDays(result);
        setLoading(false);
      },
      (err: any) => {
        if (!active) return;
        setError(err?.message || 'Failed to load imported dates');
        setLoading(false);
      },
    );
    return () => { active = false; };
    // onLoad is redefined on every render of the page that owns it, so depending on it would refetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  return (
    <Dialog
      open
      maxWidth="xs"
      fullWidth
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
    >
      <DialogTitle>Imported transaction dates — {accountName}</DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error">{error}</Alert>
        )}

        {!loading && !error && days?.length === 0 && (
          <Alert severity="info">
            No transactions for this account yet. Import a transaction report, or wait for
            transactions to arrive on their own.
          </Alert>
        )}

        {!loading && !error && !!days?.length && (
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Transactions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {days.map((day) => (
                  <TableRow key={day.date} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{day.date}</TableCell>
                    <TableCell align="right">{day.total.toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
